import React, { useMemo, useState } from "react";
import { Badge, Checkbox, Button, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, TextField, Tooltip, Box, Stack } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import SelectAllIcon from "@mui/icons-material/SelectAll";

interface FiltersMenuProps {
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
}

const FiltersMenu: React.FC<FiltersMenuProps> = ({ options, value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [query, setQuery] = useState("");
  const open = Boolean(anchorEl);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(n => n.toLowerCase().includes(q));
  }, [options, query]);

  const toggleTag = (name: string) => {
    if (value.includes(name)) onChange(value.filter(v => v !== name));
    else onChange([...value, name]);
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelectAll = () => onChange(options);
  const handleClear = () => onChange([]);

  const selectedCount = value.length;

  return (
    <>
      <Tooltip title="Filters">
        <IconButton color={selectedCount ? "primary" : "default"} onClick={handleOpen} size="large">
          <Badge color="primary" badgeContent={selectedCount} overlap="circular">
            <FilterListIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 420,
            overflow: "auto",
            p: 0,
            "& .MuiMenu-list": { p: 0 },
          },
        }}
      >
        <Box sx={{ position: "sticky", top: 0, zIndex: 1, bgcolor: "background.paper", borderBottom: 1, borderColor: "divider", p: 1 }}>
          <Stack spacing={1}>
            <TextField
              size="small"
              placeholder="Search tag names"
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoFocus
              fullWidth
            />
            <Stack direction="row" spacing={1} justifyContent="space-between">
              <Button size="small" startIcon={<SelectAllIcon fontSize="small" />} onClick={handleSelectAll} disabled={options.length === value.length}>
                Select all
              </Button>
              <Button size="small" color="inherit" startIcon={<ClearAllIcon fontSize="small" />} onClick={handleClear} disabled={value.length === 0}>
                Clear
              </Button>
            </Stack>
          </Stack>
        </Box>
        <Box sx={{ px: 2, py: 1, color: "text.secondary", typography: "overline" }}>Tags</Box>
        {filteredOptions.length === 0 ? (
          <MenuItem disabled dense>
            <ListItemText primary="No tags found" />
          </MenuItem>
        ) : (
          filteredOptions.map(name => (
            <MenuItem key={name} onClick={() => toggleTag(name)} dense>
              <ListItemIcon>
                <Checkbox edge="start" checked={value.includes(name)} tabIndex={-1} disableRipple />
              </ListItemIcon>
              <ListItemText primary={name} />
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

export default FiltersMenu;
